import { Injectable } from '@angular/core';

import { DateTime } from 'luxon';

import { APP_DATA_KEYS, SettingDateFormat } from '../constants/app-data.constants';

import { LoggedInUserRoleFlags } from '../interfaces/logged-in-user-role-flags.interface';
import { LoggedInUserProfile } from '../interfaces/logged-in-user-profile.interface';
import { ServerAppSettings } from '../interfaces/server-app-settings.interface';
import { MaatSorDescriptionSet } from '../interfaces/maat-sor-description-set.interface';

import { ServiceWorkerControl } from '../helpers/service-worker-control';
import { LoggedInUserRoleFlagsHelper } from '../helpers/logged-in-user-role-flags-helper';

import { AsyncUserService } from './async-user.service';
import { AppDataService } from './app-data.service';

@Injectable({
  providedIn: 'root'
})
export class ServiceWorkerService {

  private serviceWorkerControl: ServiceWorkerControl;

  constructor(
    private asyncUserService: AsyncUserService,
    private appDataService: AppDataService) {

    this.serviceWorkerControl = new ServiceWorkerControl(this.appDataService.getAppData(APP_DATA_KEYS.logLevel));
  }

  // This method waits until the service worker is "ready".
  // The service worker is "ready" when:
  // - it has become 'activated' (i.e.: messages to and from the client application are starting to work)
  // - it has claimed control over the client application (i.e.: the interceptions of client application HTTP fetches are starting to work)
  // - it is aware of the currently logged-in user (i.e.: it has correctly initialised its own client registry and user registry)
  async waitForServiceWorker(): Promise<{ serviceWorker: ServiceWorker, loggedInUserProfile: LoggedInUserProfile }> {

    const serviceWorker = await this.serviceWorkerControl.wait();
    const loggedInUserProfile = await this.asyncUserService.getLoggedInUserProfileOrRedirect();

    const { serverAppSettings, hiipComplianceOfficerId, publicHolidayDates, maatSorDescriptionSet, roles, position } = loggedInUserProfile;
    this.appDataService.setAppData<ServerAppSettings>(APP_DATA_KEYS.serverAppSettings, serverAppSettings);
    this.appDataService.setAppData<string>(APP_DATA_KEYS.hiipComplianceOfficerId, hiipComplianceOfficerId);
    this.appDataService.setAppData<LoggedInUserRoleFlags>(APP_DATA_KEYS.loggedInUserRoleFlags, (new LoggedInUserRoleFlagsHelper(roles)).roleFlags);
    this.appDataService.setAppData<DateTime[]>(APP_DATA_KEYS.publicHolidayDates, publicHolidayDates?.map(date => DateTime.fromFormat(date, SettingDateFormat)) ?? []);
    this.appDataService.setAppData<MaatSorDescriptionSet>(APP_DATA_KEYS.maatSorDescriptionSet, maatSorDescriptionSet);
    this.appDataService.setAppData<string>(APP_DATA_KEYS.position, position);

    return { serviceWorker, loggedInUserProfile };
  }
}
